import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Undo2, Trash2, Download, Copy, Check, X, Square, Droplet } from 'lucide-react';
import { toast } from 'sonner';

// Lets a tech black out OR blur sensitive regions of a screenshot, then download or
// copy the redacted PNG. Works entirely on a canvas — the original image is untouched.
export const RedactionCanvas = ({ src, onExit }) => {
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const drawingRef = useRef(null); // { x, y, w, h } while dragging (canvas coords)
  const [rects, setRects] = useState([]); // each: { x, y, w, h, type: 'black'|'blur' }
  const [mode, setMode] = useState('black');
  const [ready, setReady] = useState(false);
  const [copied, setCopied] = useState(false);
  const modeRef = useRef('black');
  modeRef.current = mode;

  // Load the image and size the canvas to its natural resolution.
  useEffect(() => {
    let cancelled = false;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (cancelled) return;
      imageRef.current = img;
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
      }
      setRects([]);
      setReady(true);
    };
    img.onerror = () => setReady(false);
    img.src = src;
    return () => { cancelled = true; };
  }, [src]);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imageRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    rects.forEach((r) => {
      if (r.type === 'blur') {
        ctx.save();
        ctx.beginPath();
        ctx.rect(r.x, r.y, r.w, r.h);
        ctx.clip();
        ctx.filter = `blur(${Math.min(20, Math.max(6, Math.round(Math.min(r.w, r.h) / 6)))}px)`;
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        ctx.filter = 'none';
        ctx.restore();
      } else {
        ctx.fillStyle = '#000';
        ctx.fillRect(r.x, r.y, r.w, r.h);
      }
    });
    const d = drawingRef.current;
    if (d) {
      ctx.save();
      ctx.fillStyle = modeRef.current === 'blur' ? 'rgba(59,130,246,0.35)' : 'rgba(0,0,0,0.55)';
      ctx.fillRect(d.x, d.y, d.w, d.h);
      ctx.strokeStyle = modeRef.current === 'blur' ? '#3b82f6' : '#000';
      ctx.lineWidth = 2;
      ctx.strokeRect(d.x, d.y, d.w, d.h);
      ctx.restore();
    }
  }, [rects]);

  useEffect(() => { if (ready) redraw(); }, [ready, rects, redraw]);

  const toCanvasCoords = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const point = e.touches ? e.touches[0] : e;
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return { x: (point.clientX - rect.left) * scaleX, y: (point.clientY - rect.top) * scaleY };
  };

  const start = (e) => {
    e.preventDefault();
    const p = toCanvasCoords(e);
    drawingRef.current = { x: p.x, y: p.y, w: 0, h: 0, ox: p.x, oy: p.y };
  };
  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const p = toCanvasCoords(e);
    const d = drawingRef.current;
    drawingRef.current = {
      ...d,
      x: Math.min(d.ox, p.x),
      y: Math.min(d.oy, p.y),
      w: Math.abs(p.x - d.ox),
      h: Math.abs(p.y - d.oy),
    };
    redraw();
  };
  const end = () => {
    const d = drawingRef.current;
    drawingRef.current = null;
    if (d && d.w > 3 && d.h > 3) {
      setRects((r) => [...r, { x: d.x, y: d.y, w: d.w, h: d.h, type: modeRef.current }]);
    } else {
      redraw();
    }
  };

  const undo = () => setRects((r) => r.slice(0, -1));
  const clear = () => setRects([]);

  const exportBlob = () => new Promise((resolve, reject) => {
    const canvas = canvasRef.current;
    if (!canvas) return reject(new Error('no canvas'));
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob failed'))), 'image/png');
  });

  const downloadRedacted = async () => {
    try {
      const blob = await exportBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `redacted-${Date.now()}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Could not export redacted image');
    }
  };

  const copyRedacted = async () => {
    try {
      if (typeof ClipboardItem === 'undefined' || !navigator.clipboard?.write) throw new Error('unsupported');
      const blob = await exportBlob();
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      setCopied(true);
      toast.success('Redacted image copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      const msg = e && e.name === 'NotAllowedError' ? 'Clipboard permission denied by the browser' : 'Copy to clipboard is not supported in this browser';
      toast.error(msg);
    }
  };

  const btn = 'inline-flex items-center gap-1 rounded-md bg-white/15 hover:bg-white/30 text-white px-3 py-1.5 text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed';

  return (
    <div className="flex flex-col items-center gap-3 w-full h-full justify-center" onClick={(e) => e.stopPropagation()}>
      <div data-testid="redaction-toolbar" className="flex flex-wrap items-center justify-center gap-2">
        <span className="text-white/70 text-xs mr-1">Drag to redact sensitive areas</span>
        <div className="flex items-center rounded-md bg-white/10 p-0.5">
          <button type="button" data-testid="redact-mode-black-btn" onClick={() => setMode('black')} className={`inline-flex items-center gap-1 rounded px-2.5 py-1 text-sm transition-colors ${mode === 'black' ? 'bg-white/30 text-white' : 'text-white/70 hover:text-white'}`}>
            <Square className="h-4 w-4" />Box
          </button>
          <button type="button" data-testid="redact-mode-blur-btn" onClick={() => setMode('blur')} className={`inline-flex items-center gap-1 rounded px-2.5 py-1 text-sm transition-colors ${mode === 'blur' ? 'bg-white/30 text-white' : 'text-white/70 hover:text-white'}`}>
            <Droplet className="h-4 w-4" />Blur
          </button>
        </div>
        <button type="button" data-testid="redact-undo-btn" onClick={undo} disabled={rects.length === 0} className={btn}><Undo2 className="h-4 w-4" />Undo</button>
        <button type="button" data-testid="redact-clear-btn" onClick={clear} disabled={rects.length === 0} className={btn}><Trash2 className="h-4 w-4" />Clear</button>
        <button type="button" data-testid="redact-download-btn" onClick={downloadRedacted} className={btn}><Download className="h-4 w-4" />Download</button>
        <button type="button" data-testid="redact-copy-btn" onClick={copyRedacted} className={btn}>{copied ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}Copy</button>
        <button type="button" data-testid="redact-exit-btn" onClick={onExit} className={btn}><X className="h-4 w-4" />Done</button>
      </div>
      <canvas
        ref={canvasRef}
        data-testid="redaction-canvas"
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
        className="max-h-[78vh] max-w-[92vw] object-contain rounded-md shadow-2xl bg-white cursor-crosshair"
        style={{ touchAction: 'none' }}
      />
    </div>
  );
};

export default RedactionCanvas;
